import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { FMEA_PROPOSAL_SEAT } from '@/versions/v1/modules/advisory/FmeaLearning.transform';
import {
  GATE_APPROVED_SURFACE,
  GATE_PRIMARY_BUTTON,
  gatePillClass,
} from '@/versions/v1/modules/advisory/gate-ui';
import { seniorMetallurgistSeatHolder, useRole } from '@/versions/v1/shared/RoleContext';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import type { FmeaLearningSessionState } from '@/versions/v1/shared/FmeaLearningEngine';
import {
  recordedEscalationIds,
  type EscalationDecisionRecord,
  type EscalationDecisionState,
} from '@/versions/v1/shared/EscalationDecisionEngine';
import { useEscalationDecisions } from '@/versions/v1/shared/EscalationDecisionProvider';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { vizToneColor } from '../_viz/viz-tone';
import { AiBandPanelBody } from './AiBand';
import { DecisionField, DecisionGate, NEUTRAL_PILL } from './LandingDecisionGate';
import { EbitdaWaterfall } from './EbitdaBridgePanel';
import { FunctionHealthStrip } from './FunctionHealthStrip';
import { GmLeadBand, GmMiniRow } from './GmLeadBand';
import { gmExecMiniRow, gmPerfMiniRow, type GmTabId } from './GmLeadBand.transform';
import type { AiBandPanel, ByAreaRow, PerformanceViewModel } from './Performance.transform';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import {
  budgetRiskRows,
  decisionRailHead,
  decisionRecordLine,
  escalationCounts,
  fmeaRailCards,
  forecastCostBasis,
  gmLeverageInsight,
  mtdPerformanceVsBudgetRows,
  threeScenarioForecast,
  type EscalationCard,
  type FmeaRailCard,
} from './GmTabs.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

/** A leading ASCII hyphen on a signed figure renders as a true minus. Display only. */
function typographicMinus(value: string): string {
  return value.startsWith('-') ? `−${value.slice(1)}` : value;
}

/** Status pill on the state tokens (comp `.pill`): state colour marks state only. */
export function statePillClass(tone: EscalationCard['dueTone']): string {
  const base =
    'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';
  switch (tone) {
    case 'bad':
      return cn(base, 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)]');
    case 'warn':
      return cn(base, 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]');
    case 'info':
      return cn(base, 'bg-[color-mix(in_srgb,var(--state-info)_12%,transparent)] text-[var(--state-info)]');
    default:
      return cn(base, 'bg-[var(--hairline)] text-ink-secondary');
  }
}

/** A titled region (comp `.card`): matte face, 13px title, meta to the right, hairlines inside. */
export function GmCard({
  title,
  meta,
  className,
  children,
  flush = false,
  ...rest
}: {
  title: string;
  meta?: ReactNode;
  className?: string;
  children: ReactNode;
  /** Rows run edge to edge under a hairline-ruled head (comp `.card.flush`) —
   *  tables and lists that draw their own 16px gutters. Default off. */
  flush?: boolean;
} & Record<`data-${string}`, string | boolean | undefined>) {
  if (flush) {
    return (
      <section className={cn('card-matte-2 min-w-0 overflow-hidden rounded-2xl', className)} {...rest}>
        <h3 className="flex flex-wrap items-center gap-2 border-b border-[var(--hairline)] px-4 pb-2.5 pt-3 text-[13px] font-semibold text-ink-primary">
          <span className="min-w-0">{title}</span>
          {meta ? <span className="ml-auto flex flex-wrap items-center gap-2 text-xs font-normal tabular-nums text-muted">{meta}</span> : null}
        </h3>
        {children}
      </section>
    );
  }
  return (
    <section className={cn('card-matte-2 min-w-0 rounded-2xl px-4 pb-3 pt-2.5', className)} {...rest}>
      <h3 className="mb-2 flex flex-wrap items-center gap-2 text-[13px] font-semibold text-ink-primary">
        <span className="min-w-0">{title}</span>
        {meta ? <span className="ml-auto flex flex-wrap items-center gap-2 text-xs font-normal text-muted">{meta}</span> : null}
      </h3>
      {children}
    </section>
  );
}

// ---- Executive summary -------------------------------------------------

function BridgeCard({ state }: { state: SessionState }) {
  const insight = gmLeverageInsight(state);
  return (
    <GmCard title="EBITDA bridge — Budget to actual" meta={`1–7 Jun · ${CURRENCY_GLYPH}K`} className="h-full" data-gm-bridge>
      <EbitdaWaterfall />
      <div className="mt-2 flex items-start gap-3 border-t border-[var(--hairline)] pt-2 text-[13px]">
        <ActorChip actor="Engine" />
        <div className="min-w-0">
          <p className="font-semibold text-ink-primary">{insight.headline}</p>
          <p className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary">
            {insight.comparison} · <span className="font-semibold text-ink-primary">{insight.lead}</span>
          </p>
        </div>
      </div>
    </GmCard>
  );
}

function MtdCard({ state }: { state: SessionState }) {
  const rows = mtdPerformanceVsBudgetRows(state);
  const toneClass = (tone: string) =>
    tone === 'bad'
      ? 'text-[var(--state-blocked)]'
      : tone === 'warn'
        ? 'text-[var(--state-warning)]'
        : tone === 'ok'
          ? 'text-[var(--state-online)]'
          : 'text-ink-secondary';
  return (
    <GmCard title="MTD performance vs budget" meta="1–7 Jun" className="h-full" data-gm-mtd>
      <table className="w-full border-collapse text-[13px] tabular-nums">
        <thead>
          <tr className="border-b border-[var(--hairline-strong)]">
            {['Metric', 'Budget', 'Actual', 'vs budget'].map((head, i) => (
              <th
                key={head}
                className={cn(
                  'whitespace-nowrap px-2 py-1 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted',
                  i === 0 ? 'text-left' : 'text-right',
                )}
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const total = i === rows.length - 1;
            return (
              <tr
                key={row.id}
                className={cn(
                  'border-b border-[var(--hairline)] last:border-b-0',
                  total ? 'border-t border-t-[var(--hairline-strong)]' : undefined,
                )}
              >
                <td className={cn('px-2 py-[5px] font-medium text-ink-primary', total && 'font-semibold')}>{row.metric}</td>
                <td className="px-2 py-[5px] text-right text-ink-secondary">{row.budget}</td>
                <td className={cn('px-2 py-[5px] text-right', total ? 'font-semibold text-ink-primary' : 'text-ink-secondary')}>
                  {row.actual}
                </td>
                <td className={cn('px-2 py-[5px] text-right font-semibold', toneClass(row.tone))}>{row.variance}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GmCard>
  );
}

// ---- Escalations -----------------------------------------------------------

function DecisionCard({
  card,
  rank,
  record,
  canDecide,
  onRecord,
}: {
  card: EscalationCard;
  rank: number;
  record?: EscalationDecisionRecord;
  canDecide: boolean;
  onRecord: (decision: string) => void;
}) {
  const [choice, setChoice] = useState(card.options[0] ?? '');
  const pattern = card.kind === 'pattern';
  const line = record ? decisionRecordLine(card, record) : undefined;
  const hot = rank === 1;
  return (
    <article
      className="grid grid-cols-[22px_minmax(0,1fr)] gap-x-3 border-b border-[var(--hairline)] py-3 last:border-b-0"
      data-escalation-card={card.id}
      data-escalation-kind={card.kind}
    >
      <span
        className={cn(
          'pt-px text-center text-xs font-bold tabular-nums',
          hot ? 'text-[var(--color-accent-600)] dark:text-[var(--color-accent-300)]' : 'text-muted',
        )}
      >
        {rank}
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <p className={cn('text-[13.5px]', pattern ? 'font-medium text-ink-secondary' : 'font-semibold text-ink-primary')}>
            {card.title}
          </p>
          <span className={statePillClass(card.dueTone)}>
            {pattern ? `Pattern · review ${card.due}` : `Due ${card.due}`}
          </span>
        </div>
        <p className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary">
          {card.financialParts.map((part, i) =>
            part.strong ? (
              <span key={i} className="font-semibold text-ink-primary">
                {part.text}
              </span>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}
        </p>
        {card.hypotheses ? (
          <ol className="mt-1 list-decimal pl-[18px] text-[12.5px] text-ink-secondary">
            {card.hypotheses.map((hypothesis) => (
              <li key={hypothesis}>{hypothesis}</li>
            ))}
          </ol>
        ) : null}
        {card.owner || card.code ? (
          <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
            {card.code ? <span className="font-medium tracking-[0.04em] text-muted">{card.code}</span> : null}
            {card.owner ? <span>{card.owner}</span> : null}
          </p>
        ) : null}
        <DecisionGate
          lead={pattern ? 'Owner’s next step:' : 'Decision needed:'}
          text={card.decision}
          options={card.options}
          value={record?.decision ?? choice}
          onChoose={setChoice}
          selectLabel={`Decision for ${card.title}`}
          seats={card.seats}
          date={card.date}
          recorded={Boolean(record)}
          canDecide={canDecide}
          buttonLabel="Record decision"
          buttonTone={pattern ? 'ghost' : 'primary'}
          onRecord={() => onRecord(choice)}
          waitingLabel="Awaiting decision"
          recordLine={line}
          dataKey="escalation"
        />
      </div>
    </article>
  );
}

/**
 * K13b: the rail's living-FMEA card. It carries the case page's second gate —
 * the Senior met seat approves the proposal the Metallurgist's action raised —
 * so the one decision reads in two places. The card only exists while the
 * proposal waits or after the seat has approved it; it is never at rest.
 */
function FmeaDecisionCard({
  card,
  rank,
  caseHref,
  canApprove,
  onApprove,
}: {
  card: FmeaRailCard;
  rank: number;
  caseHref: string;
  canApprove: boolean;
  onApprove: () => void;
}) {
  const approved = card.status === 'approved';
  return (
    <article
      className="grid grid-cols-[22px_minmax(0,1fr)] gap-x-3 border-b border-[var(--hairline)] py-3 last:border-b-0"
      data-fmea-rail-card={card.caseId}
      data-fmea-rail-status={card.status}
    >
      <span
        className={cn(
          'pt-px text-center text-xs font-bold tabular-nums',
          rank === 1 ? 'text-[var(--color-accent-600)] dark:text-[var(--color-accent-300)]' : 'text-muted',
        )}
      >
        {rank}
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 text-[13.5px] font-semibold text-ink-primary">
            {card.failureMode}
            <Link
              to={caseHref}
              data-fmea-rail-case={card.caseId}
              className="ml-2 font-mono text-xs font-medium tracking-wide text-accent-700 hover:underline dark:text-accent-400"
            >
              {card.caseCode}
            </Link>
          </p>
          {approved ? (
            <span className={gatePillClass('approved')} data-fmea-rail-stamp>
              {card.stamp}
            </span>
          ) : (
            <span className={statePillClass('warn')} data-fmea-rail-pill>
              Awaiting seat
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary" data-fmea-rail-change>
          <span className="font-medium tracking-[0.04em] text-muted">O</span>{' '}
          {card.occurrence.previous ? <del className="mr-1.5 text-muted">{card.occurrence.previous}</del> : null}{' '}
          <span aria-hidden="true">→</span>{' '}
          <span className="font-semibold text-ink-primary">{card.occurrence.value}</span>
          {' · '}
          <span className="font-medium tracking-[0.04em] text-muted">RPN</span>{' '}
          {card.rpn.previous ? <del className="mr-1.5 text-muted">{card.rpn.previous}</del> : null}{' '}
          <span aria-hidden="true">→</span>{' '}
          <span className="font-semibold text-ink-primary">{card.rpn.value}</span>
        </p>
        {card.learned.length > 0 ? (
          <ul className="mt-1 space-y-0.5 text-[12.5px]">
            {card.learned.map((line) => (
              <li key={line.kind} data-fmea-rail-learned={line.kind} className="font-semibold text-[var(--state-online)]">
                + {line.text}
              </li>
            ))}
          </ul>
        ) : null}
        <div
          className={cn(
            'mt-2 flex flex-col gap-2 rounded-xl border py-2 pl-3 pr-2 transition-colors duration-300',
            approved ? GATE_APPROVED_SURFACE : 'border-[var(--hairline-strong)] bg-[var(--surface-2)]',
          )}
          data-fmea-rail-gate={approved ? 'approved' : 'open'}
        >
          <p className="text-[12.5px] text-ink-secondary">
            <span className="font-semibold text-ink-primary">Decision needed:</span> Approve the living-FMEA change this
            case taught — the row takes both learned lines and occurrence rises {card.occurrence.previous} →{' '}
            {card.occurrence.value}.
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <DecisionField label="Seat">
              <ActorChip actor="Human">{card.seatLabel}</ActorChip>
            </DecisionField>
            {approved ? (
              <span className="ml-auto px-2 text-xs font-semibold text-[var(--state-online)]">Approved</span>
            ) : canApprove ? (
              <button
                type="button"
                className={cn(GATE_PRIMARY_BUTTON, 'ml-auto h-7 px-3')}
                onClick={onApprove}
              >
                Approve · {FMEA_PROPOSAL_SEAT}
              </button>
            ) : (
              <span className="ml-auto px-2 text-xs font-medium text-muted" data-fmea-rail-waiting>
                Awaiting approval
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function DecisionRail({
  state,
  records,
  recorded,
  fmeaLearning,
  caseHref,
  onRecord,
  onApproveFmea,
}: {
  state: SessionState;
  records: EscalationDecisionState;
  recorded: ReadonlySet<string>;
  fmeaLearning: FmeaLearningSessionState | undefined;
  caseHref: (caseId: string) => string;
  onRecord: (card: EscalationCard, decision: string) => void;
  onApproveFmea: (caseId: string) => void;
}) {
  const { can } = useRole();
  const canDecide = can('escalation:decide');
  const canApproveFmea = can('fmea:approve-proposal');
  const { cards, decisions, patterns } = escalationCounts(state, recorded, fmeaLearning);
  const head = decisionRailHead(state, recorded);
  const fmeaCards = fmeaRailCards(fmeaLearning, {
    canApprove: canApproveFmea,
    // The seat holder's persona value is also its picker label (GM, Senior metallurgist).
    seatHolderLabel: seniorMetallurgistSeatHolder(),
  });
  return (
    <GmCard
      title="Decisions for the manager"
      data-gm-decisions
      meta={
        <>
          <span className={NEUTRAL_PILL}>{`${decisions} decision${decisions === 1 ? '' : 's'}`}</span>
          {patterns > 0 ? <span className={NEUTRAL_PILL}>{`${patterns} pattern${patterns === 1 ? '' : 's'}`}</span> : null}
        </>
      }
    >
      <div className="mb-1 grid grid-cols-1 rounded-xl border border-[var(--hairline)] bg-[var(--surface-2)] sm:grid-cols-[1fr_1.25fr_1.4fr]">
        {head.map((cell) => (
          <div
            key={cell.id}
            className="flex min-w-0 flex-col gap-px border-b border-[var(--hairline)] px-3 py-2 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0"
          >
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted">{cell.label}</span>
            <span
              className={cn(
                'text-[17px] font-semibold leading-tight tabular-nums',
                cell.tone === 'bad' ? 'text-[var(--state-blocked)]' : 'text-ink-primary',
              )}
            >
              {cell.value}
            </span>
            <span className="text-xs leading-snug tabular-nums text-muted">{cell.caption}</span>
          </div>
        ))}
      </div>
      <div>
        {fmeaCards.map((card, index) => (
          <FmeaDecisionCard
            key={card.id}
            card={card}
            rank={index + 1}
            caseHref={caseHref(card.caseId)}
            canApprove={canApproveFmea}
            onApprove={() => onApproveFmea(card.caseId)}
          />
        ))}
        {cards.map((card, index) => (
          <DecisionCard
            key={card.id}
            card={card}
            rank={fmeaCards.length + index + 1}
            record={records.get(card.id)}
            canDecide={canDecide}
            onRecord={(decision) => onRecord(card, decision)}
          />
        ))}
      </div>
    </GmCard>
  );
}

// ---- Performance vs budget ------------------------------------------------

function ForecastCard() {
  const rows = threeScenarioForecast();
  const heads = ['Scenario', 'Full-year tonnes (dst)', 'vs budget', 'Rest-of-year Zn rec', 'Full-year NSR', 'Operating EBITDA'];
  return (
    <GmCard title="Three-scenario full-year forecast" meta="Planning · Year 1 · Mar 2026 – Feb 2027" data-gm-forecast>
      <p className="-mt-1 mb-1.5 text-xs text-muted">{forecastCostBasis()}</p>
      <table className="w-full border-collapse text-[13px] tabular-nums">
        <thead>
          <tr className="border-b border-[var(--hairline-strong)]">
            {heads.map((head, i) => (
              <th
                key={head}
                className={cn(
                  'px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted',
                  i === 0 ? 'min-w-[15rem] text-left' : 'text-right',
                )}
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={cn(
                'border-b border-[var(--hairline)] last:border-b-0',
                row.emphasis === 'row' ? 'bg-[var(--accent-soft)]' : undefined,
              )}
            >
              <td
                className={cn(
                  'px-2 py-2',
                  row.emphasis === 'muted' ? 'font-medium text-muted' : 'font-medium text-ink-primary',
                )}
              >
                {row.scenario}
              </td>
              <td className="px-2 py-2 text-right text-ink-secondary">{row.tonnes}</td>
              <td className="px-2 py-2 text-right text-ink-secondary">{typographicMinus(row.vsBudget)}</td>
              <td className="px-2 py-2 text-right text-ink-secondary">{row.znRecH2}</td>
              <td className="px-2 py-2 text-right text-ink-secondary">{row.nsr}</td>
              <td className="px-2 py-2 text-right font-semibold text-ink-primary">{row.ebitda}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </GmCard>
  );
}

function RiskCard() {
  const rows = budgetRiskRows();
  return (
    <GmCard title="Key risks to full-year delivery" className="h-full" data-gm-risks>
      <ul>
        {rows.map((row) => (
          <li
            key={row.id}
            className="grid grid-cols-[10px_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 border-b border-[var(--hairline)] py-2 last:border-b-0"
          >
            <span
              aria-hidden="true"
              className="mt-1.5 size-2 rounded-full"
              style={{ backgroundColor: vizToneColor(row.tone) }}
            />
            <span className="text-[13px] font-semibold text-ink-primary">{row.label}</span>
            <span className="col-start-2 text-xs tabular-nums text-muted">{row.detail}</span>
          </li>
        ))}
      </ul>
    </GmCard>
  );
}

/** An AI band panel in the landing's card grammar (comp `.card` h4 · cmp · prov). */
function AiPanelCard({ panel }: { panel: AiBandPanel }) {
  return (
    <GmCard
      title={panel.title}
      className="h-full"
      data-gm-ai-panel={panel.id}
      meta={
        panel.subtitle || panel.provenance ? (
          <>
            {panel.subtitle ? <span>{panel.subtitle}</span> : null}
            {panel.provenance ? (
              <ProvenanceChip
                text={panel.provenanceChip ?? panel.provenance}
                title={panel.provenanceChip ? panel.provenance : undefined}
                compact
              />
            ) : null}
          </>
        ) : undefined
      }
    >
      <AiBandPanelBody panel={panel} wrapClauses />
    </GmCard>
  );
}

// ---- Composition -------------------------------------------------------------

/**
 * The Concentrator Manager's landing (komatsu.K12e #647, locked comp
 * `gm-landing.html` B with C's decision rail): the lead band's three cells are
 * the tab bar; each view keeps its figure row, body and function health.
 */
export function GmLanding({
  state,
  model,
  projectSlug,
  byAreaRows,
}: {
  state: SessionState;
  model: PerformanceViewModel;
  projectSlug: string;
  byAreaRows: readonly ByAreaRow[];
}) {
  const [tab, setTab] = useState<GmTabId>('exec');
  const { records, record } = useEscalationDecisions();
  const { fmeaLearning, approveFmeaProposal } = useAdvisory();
  const recorded = useMemo(() => recordedEscalationIds(records), [records]);
  const caseHref = (caseId: string) => `/projects/${projectSlug}/advisory/case/${caseId}`;
  const health = (layout: 'joined' | 'stack') =>
    model.functionHealth ? (
      <FunctionHealthStrip
        read={model.functionHealth}
        byAreaRows={byAreaRows}
        planAchievement={model.planAchievement}
        byAreaBasisLabel={model.byAreaBasisLabel}
        layout={layout}
      />
    ) : null;
  const briefing = model.aiBand?.panels.find((panel) => panel.id === 'ai-briefing');
  const lower = model.aiBand?.panels.filter((panel) => panel.id !== 'ai-briefing') ?? [];

  return (
    <div className="space-y-3">
      <p className="-mt-3 text-sm text-muted">{model.title}</p>
      <Commentable name="Kpis">
        <GmLeadBand
          state={state}
          tab={tab}
          recorded={recorded}
          fmeaLearning={fmeaLearning}
          onTabChange={setTab}
          advisoryHref={projectSidebarHref(projectSlug, 'advisory')}
        />
      </Commentable>
      <div id="gm-landing-panel" role="tabpanel" aria-labelledby={`gm-tab-${tab}`} className="space-y-3">
        {tab === 'exec' ? (
          <>
            <GmMiniRow items={gmExecMiniRow(state)} />
            <Commentable name="Primary">
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 lg:col-span-6 min-[1400px]:col-span-7">
                  <BridgeCard state={state} />
                </div>
                <div className="col-span-12 lg:col-span-6 min-[1400px]:col-span-5">
                  <MtdCard state={state} />
                </div>
              </div>
            </Commentable>
            {health('joined')}
          </>
        ) : null}
        {tab === 'esc' ? (
          <Commentable name="Issues">
            <div className="grid grid-cols-12 gap-3">
              <div id="walkthrough-gm-decisions" className="col-span-12 lg:col-span-8">
                <DecisionRail
                  state={state}
                  records={records}
                  recorded={recorded}
                  fmeaLearning={fmeaLearning}
                  caseHref={caseHref}
                  onRecord={(card, decision) => record(card.id, decision)}
                  onApproveFmea={approveFmeaProposal}
                />
              </div>
              <div className="col-span-12 lg:col-span-4">{health('stack')}</div>
            </div>
          </Commentable>
        ) : null}
        {tab === 'perf' ? (
          <>
            <GmMiniRow items={gmPerfMiniRow()} />
            {health('joined')}
            <Commentable name="AiBand">
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12">
                  <ForecastCard />
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <RiskCard />
                </div>
                {briefing ? (
                  <div className="col-span-12 lg:col-span-6">
                    <AiPanelCard panel={briefing} />
                  </div>
                ) : null}
                {lower.map((panel) => (
                  <div key={panel.id} className="col-span-12 lg:col-span-4">
                    <AiPanelCard panel={panel} />
                  </div>
                ))}
              </div>
            </Commentable>
          </>
        ) : null}
      </div>
    </div>
  );
}
